import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Input                                                             */
/* ------------------------------------------------------------------ */

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Visible label text. */
  label?: string;
  /** Help text below the input. */
  hint?: string;
  /** Error message. When set, the input shows the error state. */
  error?: string;
  /** Input sizing. */
  inputSize?: "sm" | "md" | "lg";
}

const css = /* css */ `
  .sf-field {
    display: flex;
    flex-direction: column;
    gap: var(--sf-space-1);
    font-family: var(--sf-font-sans);
  }

  .sf-field__label {
    font-size: var(--sf-text-sm);
    font-weight: var(--sf-weight-medium);
    color: var(--sf-text);
    line-height: var(--sf-leading-tight);
  }

  .sf-field__hint {
    font-size: var(--sf-text-xs);
    color: var(--sf-text-muted);
    line-height: var(--sf-leading-normal);
  }

  .sf-field__error {
    font-size: var(--sf-text-xs);
    color: var(--sf-error);
    line-height: var(--sf-leading-normal);
    display: flex;
    align-items: center;
    gap: var(--sf-space-1);
  }

  .sf-input {
    display: block;
    width: 100%;
    border: 1px solid var(--sf-border);
    border-radius: var(--sf-radius-md);
    background: var(--sf-bg-elevated);
    color: var(--sf-text);
    font-family: var(--sf-font-sans);
    transition: border-color var(--sf-transition-fast),
                box-shadow var(--sf-transition-fast);
  }

  .sf-input::placeholder {
    color: var(--sf-text-muted);
  }

  .sf-input:hover {
    border-color: var(--sf-border-strong);
  }

  .sf-input:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: var(--sf-ring-offset);
    border-color: var(--sf-border-focus);
  }

  .sf-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--sf-bg-inset);
  }

  /* Sizes */
  .sf-input--sm { font-size: var(--sf-text-sm); padding: 6px 10px; height: 30px; }
  .sf-input--md { font-size: var(--sf-text-base); padding: 8px 12px; height: 36px; }
  .sf-input--lg { font-size: var(--sf-text-md); padding: 10px 14px; height: 42px; }

  /* Error state */
  .sf-input--error {
    border-color: var(--sf-error);
  }
  .sf-input--error:focus-visible {
    outline-color: rgba(201, 42, 42, 0.4);
    border-color: var(--sf-error);
  }
`;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hint,
      error,
      inputSize = "md",
      className,
      id: idProp,
      ...rest
    },
    ref,
  ) => {
    injectStyles("sf-input", css);

    const autoId = React.useId();
    const id = idProp ?? autoId;
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;

    const inputClasses = [
      "sf-input",
      `sf-input--${inputSize}`,
      error ? "sf-input--error" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="sf-field">
        {label && (
          <label className="sf-field__label" htmlFor={id}>
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          className={inputClasses}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [hintId, errorId].filter(Boolean).join(" ") || undefined
          }
          {...rest}
        />
        {hint && !error && (
          <span id={hintId} className="sf-field__hint">
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} className="sf-field__error" role="alert">
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 3.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zM8 11a1 1 0 100 2 1 1 0 000-2z" />
            </svg>
            {error}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
export { Input };
